/**
 * Mode Engine - Question Selection Orchestration
 *
 * Central orchestrator for mode-specific question selection.
 * Delegates to mode-specific selectors based on mode configuration.
 *
 * NOTE: "Simulation" mode removed — merged with Assessment as "Toets" in Learning Lab
 */

import { type Question } from '../content'
import { AppState } from '../app/types'
import { GroupId } from '../config/systemConfig'
import {
  ModeId,
  FluencyConfig,
  SprintConfig,
  ChallengeConfig,
  ExperimentsConfig,
  FreePracticeConfig,
} from './types'
import {
  getFluencyQuestions,
  getSprintQuestions,
  getChallengeQuestions,
  getExperimentsQuestions,
} from './modeSelectors'
import { getAllQuestions } from '../content'

/**
 * Result of creating a mode session — contains the selected questions
 */
export interface ModeSessionResult {
  sessionId: string
  modeId: ModeId
  questions: Question[]
  config:
    | FluencyConfig
    | SprintConfig
    | ChallengeConfig
    | ExperimentsConfig
    | FreePracticeConfig
}

/**
 * Create a new mode session with selected questions
 */
export function createModeSession(
  modeId: ModeId,
  config:
    | FluencyConfig
    | SprintConfig
    | ChallengeConfig
    | ExperimentsConfig
    | FreePracticeConfig,
  state: AppState,
  selectedGroup: GroupId,
): ModeSessionResult {
  const sessionId = `${modeId}_${Date.now()}`
  const allQuestions = getAllQuestions()

  // Select questions based on mode type
  const questions = selectQuestionsForMode(
    modeId,
    config,
    allQuestions,
    state,
    selectedGroup,
  )
  if (questions.length === 0) {
    throw new Error(`No questions available for mode ${modeId}`)
  }
  return {
    sessionId,
    modeId,
    questions,
    config,
  }
}

/**
 * Select questions for a specific mode
 */
function selectQuestionsForMode(
  modeId: ModeId,
  config:
    | FluencyConfig
    | SprintConfig
    | ChallengeConfig
    | ExperimentsConfig
    | FreePracticeConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  switch (modeId) {
    case 'fluency':
      return getFluencyQuestions(
        config as FluencyConfig,
        allQuestions,
        state,
        selectedGroup,
      )
    case 'sprint':
      return getSprintQuestions(
        config as SprintConfig,
        allQuestions,
        state,
        selectedGroup,
      )
    case 'challenge':
      return getChallengeQuestions(
        config as ChallengeConfig,
        allQuestions,
        state,
        selectedGroup,
      )
    case 'experiments':
      return getExperimentsQuestions(
        config as ExperimentsConfig,
        allQuestions,
        state,
        selectedGroup,
      )
    case 'free':
      // Free practice delegates to sprint question selection
      return getSprintQuestions(
        config as SprintConfig,
        allQuestions,
        state,
        selectedGroup,
      )
    default:
      throw new Error(`Unknown mode: ${modeId}`)
  }
}
